import React, { useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';

const LayersPicker = ({ layers, onChange }) => {
  const [localLayers, setLocalLayers] = useState(layers);
  const [expandedGroups, setExpandedGroups] = useState({ 'group1': true });

  const toggleGroupExpanded = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const toggleLayerVisibility = (layerId) => {
    const updatedLayers = localLayers.map(layer => {
      if (layer.id === layerId) {
        return { ...layer, visible: !layer.visible };
      }
      if (layer.children) {
        return {
          ...layer,
          children: layer.children.map(child =>
            child.id === layerId ? { ...child, visible: !child.visible } : child
          )
        };
      }
      return layer;
    });
    setLocalLayers(updatedLayers);
    onChange(updatedLayers);
  };

  const toggleLayerLock = (layerId) => {
    const updatedLayers = localLayers.map(layer => {
      if (layer.id === layerId) {
        return { ...layer, locked: !layer.locked };
      }
      if (layer.children) {
        return {
          ...layer,
          children: layer.children.map(child =>
            child.id === layerId ? { ...child, locked: !child.locked } : child
          )
        };
      }
      return layer;
    });
    setLocalLayers(updatedLayers);
    onChange(updatedLayers);
  };

  const LayerIcon = ({ type }) => {
    switch (type) {
      case 'text':
        return <span className="text-gray-300 font-bold" style={{ fontSize: '12px' }}>T</span>;
      case 'rectangle':
        return <div className="w-3 h-3 border border-gray-400"></div>;
      case 'circle':
        return <div className="w-3 h-3 border border-gray-400 rounded-full"></div>;
      case 'image':
        return (
          <div className="w-3 h-3 border border-gray-400 flex items-center justify-center">
            <div className="w-2 h-1.5 bg-gray-400"></div>
          </div>
        );
      case 'group':
        return (
          <div className="flex flex-col gap-0.5">
            <div className="w-3 h-0.5 bg-gray-400"></div>
            <div className="w-3 h-0.5 bg-gray-400"></div>
            <div className="w-3 h-0.5 bg-gray-400"></div>
          </div>
        );
      default:
        return <div className="w-3 h-3 bg-gray-400"></div>;
    }
  };

  const EyeIcon = ({ visible }) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="text-white">
      {visible ? (
        <>
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="1.5"/>
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/>
        </>
      ) : (
        <>
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="currentColor" strokeWidth="1.5"/>
          <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="1.5"/>
        </>
      )}
    </svg>
  );

  const LockIcon = ({ locked }) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="text-white">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="1.5"/>
      {locked ? (
        <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="1.5"/>
      ) : (
        <path d="M7 11V7a5 5 0 0 1 9.9-1" stroke="currentColor" strokeWidth="1.5"/>
      )}
    </svg>
  );

  return (
    <div className="space-y-0">
      <div className="flex items-center justify-between mb-2">
        <div></div>
        <div className="flex items-center gap-2">
          <button className="text-gray-300 hover:text-white transition-colors" style={{ fontSize: '14px' }}>
            +
          </button>
          <button className="text-gray-300 hover:text-white transition-colors" style={{ fontSize: '14px' }}>
            •••
          </button>
        </div>
      </div>

      <div className="space-y-0">
        {localLayers.map((layer, layerIndex) => (
          <div key={layer.id}>
            {layer.type === 'group' ? (
              <>
                <div className={`flex items-center justify-between py-1 px-1 hover:bg-gray-700/30 transition-colors cursor-move`}>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleGroupExpanded(layer.id);
                      }}
                      className="text-gray-300"
                    >
                      {expandedGroups[layer.id] ? (
                        <ChevronDown size={10} />
                      ) : (
                        <ChevronRight size={10} />
                      )}
                    </button>
                    <LayerIcon type={layer.type} />
                    <span className="text-gray-300" style={{ fontSize: '10px' }}>
                      {layer.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleLayerVisibility(layer.id);
                      }}
                      className="p-0.5 hover:bg-gray-600 rounded transition-colors"
                    >
                      <EyeIcon visible={layer.visible} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleLayerLock(layer.id);
                      }}
                      className="p-0.5 hover:bg-gray-600 rounded transition-colors"
                    >
                      <LockIcon locked={layer.locked} />
                    </button>
                  </div>
                </div>

                <div className="w-full h-px bg-black"></div>

                {expandedGroups[layer.id] && layer.children && (
                  <div className="ml-4">
                    {layer.children.map((child, childIndex) => (
                      <div key={child.id}>
                        <div className={`flex items-center justify-between py-1 px-1 hover:bg-gray-700/30 transition-colors cursor-move`}>
                          <div className="flex items-center gap-2">
                            <LayerIcon type={child.type} />
                            <span className="text-gray-300" style={{ fontSize: '10px' }}>
                              {child.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleLayerVisibility(child.id);
                              }}
                              className="p-0.5 hover:bg-gray-600 rounded transition-colors"
                            >
                              <EyeIcon visible={child.visible} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleLayerLock(child.id);
                              }}
                              className="p-0.5 hover:bg-gray-600 rounded transition-colors"
                            >
                              <LockIcon locked={child.locked} />
                            </button>
                          </div>
                        </div>
                        {childIndex < layer.children.length - 1 && (
                          <div className="w-full h-px bg-black"></div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className={`flex items-center justify-between py-1 px-1 hover:bg-gray-700/30 transition-colors cursor-move`}>
                <div className="flex items-center gap-2">
                  <LayerIcon type={layer.type} />
                  <span className="text-gray-300" style={{ fontSize: '10px' }}>
                    {layer.name}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleLayerVisibility(layer.id);
                    }}
                    className="p-0.5 hover:bg-gray-600 rounded transition-colors"
                  >
                    <EyeIcon visible={layer.visible} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleLayerLock(layer.id);
                    }}
                    className="p-0.5 hover:bg-gray-600 rounded transition-colors"
                  >
                    <LockIcon locked={layer.locked} />
                  </button>
                </div>
              </div>
            )}
            
            {layerIndex < localLayers.length - 1 && (
              <div className="w-full h-px bg-black"></div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default LayersPicker;
